import React, { useMemo } from 'react';
import { Search } from 'lucide-react';
import type {
  DetrixActionLine,
  DetrixBrowserResourceEntry,
  DetrixEvent,
  DetrixNetworkEntry,
  DetrixSession,
  DetrixUserStep,
} from '../../types';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { NetworkRequestInspector } from './NetworkRequestInspector';
import { BrowserResourceInspector } from './BrowserResourceInspector';
import { StepInspector } from './StepInspector';
import { ActionInspector } from './ActionInspector';
import { EventInspector } from './EventInspector';

/**
 * Details / inspector for the selected timeline row.
 * Future: this panel doubles as the narrative panel (session story + description in one place).
 */

type InspectorSelection =
  | { kind: 'step'; data: DetrixUserStep }
  | { kind: 'action'; data: DetrixActionLine }
  | { kind: 'network'; data: DetrixNetworkEntry }
  | { kind: 'resource'; data: DetrixBrowserResourceEntry }
  | { kind: 'event'; data: DetrixEvent };

interface InspectorProps {
  session: DetrixSession;
}

export const Inspector: React.FC<InspectorProps> = ({ session }) => {
  const { activeTab, selectedId } = useSessionUiStore();

  const selectedItem: InspectorSelection | null = useMemo(() => {
    if (!selectedId) return null;

    switch (activeTab) {
      case 'steps': {
        const data = session.steps?.find((s: DetrixUserStep) => s.id === selectedId);
        return data ? { kind: 'step', data } : null;
      }
      case 'actions': {
        const data = session.actions?.find((a: DetrixActionLine) => a.id === selectedId);
        return data ? { kind: 'action', data } : null;
      }
      case 'network': {
        const n = session.network.find((e: DetrixNetworkEntry) => e.id === selectedId);
        if (n) return { kind: 'network', data: n };
        const r = session.browserResources?.find((e: DetrixBrowserResourceEntry) => e.id === selectedId);
        return r ? { kind: 'resource', data: r } : null;
      }
      case 'events': {
        const data = session.events.find((e: DetrixEvent) => e.id === selectedId);
        return data ? { kind: 'event', data } : null;
      }
      default:
        return null;
    }
  }, [activeTab, selectedId, session]);

  if (!selectedItem) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-[var(--bg-panel)] p-6 text-center text-[var(--text-muted)]">
        <Search className="mb-4 h-12 w-12 opacity-10" />
        <h3 className="mb-1 text-[14px] font-semibold text-[var(--text-secondary)]">No selection</h3>
        <p className="max-w-[220px] text-[12px]">Select a row in the list to inspect details.</p>
      </div>
    );
  }

  switch (selectedItem.kind) {
    case 'network':
      return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <NetworkRequestInspector session={session} entry={selectedItem.data} />
        </div>
      );
    case 'resource':
      return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <BrowserResourceInspector session={session} entry={selectedItem.data} />
        </div>
      );
    case 'step':
      return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <StepInspector session={session} step={selectedItem.data} />
        </div>
      );
    case 'action':
      return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ActionInspector session={session} action={selectedItem.data} />
        </div>
      );
    case 'event':
      return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <EventInspector session={session} event={selectedItem.data} />
        </div>
      );
  }
};
